import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

const values = [
  {
    icon: "ri-file-text-line",
    title: "把剧情讲清楚",
    description: "我们坚持用完整、连贯的语言梳理故事脉络，而不是堆砌标签和剧透截图，让读者真正读懂一部作品。",
  },
  {
    icon: "ri-user-heart-line",
    title: "关注人物与动机",
    description: "角色分析不止于“谁是谁”，更关注他们为什么做出那样的选择，理解人物才是理解故事的关键。",
  },
  {
    icon: "ri-time-line",
    title: "追踪影视动态",
    description: "获奖、定档、口碑与幕后，我们用简洁有价值的资讯，帮你在纷杂的信息里抓住真正重要的变化。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "hello@yiluxiangxi.example" },
  { icon: "ri-weibo-line", label: "微博", value: "@一路向西影视" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "一路向西影视志" },
  { icon: "ri-contacts-book-line", label: "内容合作", value: "press@yiluxiangxi.example" },
];

export default function About() {
  useSeo({
    title: "关于我们与联系方式 | 一路向西影视",
    description:
      "了解一路向西影视内容平台的定位、发展理念、内容采集与数据来源说明，并获取邮箱与社交媒体联系方式。",
    keywords: "一路向西,关于我们,联系方式,影视内容平台,剧情介绍",
  });

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <header className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
          <i className="ri-information-line text-sm" />
          站点介绍
        </span>
        <h1 className="mt-4 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
          一路向西，认真读剧情的影视内容平台
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-loose text-foreground-700 md:text-base">
          一路向西影视内容平台专注于影视作品的剧情介绍、角色关系分析与最新影视动态。我们相信，一部作品真正的魅力，藏在它讲述的故事与塑造的人物里。因此我们不追求浮夸的标题，而是希望把每一部作品的故事讲清楚、讲完整，让每一次阅读都真正有所收获。
        </p>
      </header>

      <section className="mt-10">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们坚持的三件事</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {values.map((value) => (
            <div key={value.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-500 text-background-50">
                <i className={`${value.icon} text-2xl`} />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">{value.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{value.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 lg:col-span-2">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-database-2-line text-xl text-primary-600" />
            内容采集与数据来源说明
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-loose text-foreground-600">
            <p>
              本站为演示性质的影视资讯平台，站内展示的影视作品、剧情介绍、角色资料、影视动态与专题内容，均为编辑整理或虚构示例内容，仅用于展示平台的信息组织方式与阅读体验。
            </p>
            <p>
              目前站点未接入任何第三方影视数据接口，所有列表、筛选、搜索、收藏与推荐结果均基于站内预置数据进行前端匹配与排序，不涉及真实的数据请求，也不会产生任何接口调用成功或失败的提示。
            </p>
            <p>
              如需引用本站内容或反馈资料问题，欢迎通过下方联系方式与我们取得联系，我们会认真对待每一条建议。
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-primary-950 p-6 text-background-50">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold">
            <i className="ri-compass-3-line text-xl text-accent-400" />
            快速开始
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-primary-200">
            想马上开始浏览？可以直接进入分类页，按地区、年份与类型筛选你想了解的影视作品。
          </p>
          <Link
            to="/category/all"
            className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-layout-grid-line text-base" />
            前往全部影视
          </Link>
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系方式</h2>
        <p className="mt-2 text-sm text-foreground-600">欢迎通过以下方式与我们联系，我们会尽快回复。</p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {contacts.map((contact) => (
            <div key={contact.label} className="rounded-lg border border-background-200 bg-background-100 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-50 text-primary-600">
                <i className={`${contact.icon} text-xl`} />
              </span>
              <p className="mt-4 text-xs text-foreground-500">{contact.label}</p>
              <p className="mt-1 text-sm font-medium text-foreground-900 break-all">{contact.value}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-foreground-400">说明：以上联系方式与社交媒体账号均为演示示例，仅用于展示站点信息结构。</p>
      </section>
    </div>
  );
}